import { useNavigate } from '@tanstack/react-router'
import { Activity, ArrowRight, ShieldCheck } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { ThemeToggle } from '../header/ThemeToggle'
import { LANDING_PAGE_CONFIG } from '../../config/constants'

export function Navbar() {
    const { isAuthenticated } = useAuth()
    const navigate = useNavigate()
    const [scrolled, setScrolled] = useState(false)

    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 15
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled)
            }
        }

        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [scrolled])

    const handleAuthAction = () => {
        if (isAuthenticated) {
            navigate({ to: '/home' })
        } else {
            navigate({ to: '/login', search: { redirect: '/' } })
        }
    }

    return (
        <nav
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? 'bg-white/85 dark:bg-[#060b0b]/85 backdrop-blur-md border-b border-slate-200/80 dark:border-white/10 py-3 shadow-xs dark:shadow-black/40'
                    : 'bg-transparent py-5'
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
                {/* Brand Logo */}
                <div
                    className="flex items-center gap-3 cursor-pointer group"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                    <div className="relative w-10 h-10 flex items-center justify-center bg-[#005451] rounded-xl shadow-md shadow-[#005451]/25 group-hover:scale-105 transition-transform duration-300">
                        <Activity className="text-white" size={20} strokeWidth={2.5} />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-lg font-black tracking-tight text-slate-900 dark:text-white group-hover:text-[#005451] dark:group-hover:text-teal-400 transition-colors">
                            Options<span className="text-[#005451] dark:text-teal-400">Oracle</span>
                        </span>
                        <span className="text-[9px] font-mono font-bold tracking-widest uppercase text-slate-400 dark:text-slate-500 -mt-1">
                            QUANT TERMINAL
                        </span>
                    </div>
                </div>

                {/* Central Navigation Links */}
                <div className="hidden md:flex items-center gap-7 text-xs font-bold tracking-wide text-slate-600 dark:text-slate-300 uppercase">
                    {LANDING_PAGE_CONFIG.NAV_ITEMS.map((item) => (
                        <a
                            key={item.label}
                            href={item.href}
                            className="hover:text-[#005451] dark:hover:text-teal-400 transition-colors py-1"
                        >
                            {item.label}
                        </a>
                    ))}
                </div>

                {/* Right Action Elements */}
                <div className="flex items-center gap-3 sm:gap-4">
                    {/* Real-time Telemetry Pill */}
                    <div className="hidden lg:flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100/90 dark:bg-slate-900/80 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 text-[10px] font-mono font-bold">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        <span>LIVE 2s STREAM</span>
                    </div>

                    <ThemeToggle />

                    <button
                        onClick={handleAuthAction}
                        className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 hover:text-[#005451] dark:hover:text-teal-400 transition-colors hidden sm:block px-2"
                    >
                        {isAuthenticated ? 'Terminal Hub' : 'Sign In'}
                    </button>

                    <button
                        onClick={handleAuthAction}
                        className="group relative px-4.5 py-2 sm:px-5 sm:py-2.5 bg-[#005451] hover:bg-[#004240] text-white rounded-xl text-xs sm:text-sm font-bold shadow-md shadow-[#005451]/20 transition-all duration-300 hover:scale-105 active:scale-95 flex items-center gap-2"
                    >
                        <span>{isAuthenticated ? 'Open Terminal' : 'Launch Terminal'}</span>
                        <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                    </button>
                </div>
            </div>
        </nav>
    )
}
